import { useTranslation } from 'react-i18next';
import Alert from '@/components/ui/Alert';
import { SupportedIsraeliIdChangeRequestDtoIn } from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import styles from './IsraeliIdChangeRequest.module.css';

/** The office's last decision on a request, said once above the board. */
export interface IsraeliIdChangeDecision {
  decision: 'approved' | 'rejected';
  request: SupportedIsraeliIdChangeRequestDtoIn;
  /** Whether the mail to the coordinator went out; null when the office chose not to send one. */
  emailSent: boolean | null;
}

interface IsraeliIdChangeRequestsBannerProps {
  /** The waiting requests the office was not told about yet, oldest first. */
  pending: SupportedIsraeliIdChangeRequestDtoIn[];
  /** Turns on the board's «תיקון מספר זהות» filter. */
  onShow: () => void;
  /** These requests were told about - the line does not come back for them. */
  onSeen: (requests: SupportedIsraeliIdChangeRequestDtoIn[]) => void;
  lastDecision: IsraeliIdChangeDecision | null;
  onDismissDecision: () => void;
}

/**
 * Above the office's supporteds board (HAT-1428), the way the coordinator's board says it: new
 * coordinators' requests to correct an ID, said once (Chaya, 20.10). «הצג» turns on the board's own
 * filter chip, which stays like any other filter; after «הצג» or × the line is gone for these
 * requests. After a decision, what it did and whether the coordinator was mailed: a decision stands
 * even when its mail does not go out, and then the office has to tell the coordinator.
 */
const IsraeliIdChangeRequestsBanner = ({
  pending, onShow, onSeen, lastDecision, onDismissDecision,
}: IsraeliIdChangeRequestsBannerProps) => {
  const { t } = useTranslation();
  const prefix = 'israeliIdChangeRequests';

  const decisionText = () => {
    if (!lastDecision) return '';
    const { request, emailSent } = lastDecision;
    const coordinator = request.requestedByName || request.requestedByEmail;
    return [
      t(`${prefix}.decided.${lastDecision.decision}`, { name: request.supportedName ?? '', id: request.requestedIsraeliId }),
      emailSent === true ? t(`${prefix}.decided.mailSent`, { name: coordinator }) : null,
      emailSent === false ? t(`${prefix}.mailFailed`, { name: coordinator }) : null,
    ].filter(Boolean).join(' ');
  };

  if (!lastDecision && pending.length === 0) return null;

  return (
    <div className={styles.bannerStack}>
      {lastDecision && (
        <Alert
          key={`decided-${lastDecision.request.id}`}
          type={lastDecision.emailSent === false ? 'warning' : 'success'}
          inline
          title={decisionText()}
          onClose={onDismissDecision}
          className={styles.banner}
        />
      )}
      {pending.length > 0 && (
        <Alert
          key="pending"
          type="warning"
          inline
          title={t(`${prefix}.banner.pending`, { count: pending.length, name: pending[0].supportedName ?? '' })}
          action={{
            label: t(`${prefix}.banner.show`),
            onClick: () => {
              onSeen(pending);
              onShow();
            },
          }}
          onClose={() => onSeen(pending)}
          className={styles.banner}
        />
      )}
    </div>
  );
};

export default IsraeliIdChangeRequestsBanner;
